import { useEffect, useState } from "react";
import { ArrowSquareOut, Info } from "@phosphor-icons/react";
import api from "../lib/api";
import { publicImageUrlForCompetition } from "../lib/publicImage";

export function sourceActionForCompetition(comp) {
  if (comp.cta_target === "none") return null;
  const overlay = comp.source_overlay || {};
  const approvedCta = ["operator", "affiliate", "partner"].includes(comp.cta_target);
  const ctaHref = comp.cta_destination_url || comp.outbound_url || comp.public_click_url;
  const href = approvedCta && ctaHref
    ? ctaHref
    : comp.source_competition_url || comp.competition_url || overlay.href;
  if (!href) return null;
  try {
    const url = new URL(href);
    if (url.protocol !== "https:" || url.username || url.password) return null;
    if (!approvedCta && (url.pathname === "/" || !url.pathname)) return null;
  } catch {
    return null;
  }
  return {
    ...overlay,
    href,
    is_affiliate: approvedCta && ctaHref === href
      ? comp.cta_target === "affiliate" || comp.is_affiliate_link === true
      : overlay.href === href && overlay.is_affiliate === true,
  };
}

/**
 * <PrizeImage />
 *
 * Single source of truth for rendering a competition prize image with the
 * iter-9 asset-resolution states:
 *
 *   - source_match    : operator-sourced image + clickable source overlay
 *   - exact_match     : admin-verified image, no chip
 *   - close_match     : curated registry image + "Representative image" chip
 *   - generated_render: future render fallback + "Representative render" chip
 *   - brand_fallback  : premium navy/gold brand tile (text-based, no logo)
 *   - category_fallback: same tile, category text only
 *   - placeholder / pending_review / mismatch_blocked: subtle "Source image pending"
 *
 * The component never displays a hotlinked image whose status is unsafe — the
 * backend already strips `image_url` in those cases.
 */
export default function PrizeImage({ comp, variant = "card", showOutboundOverlay = true }) {
  const status = comp.asset_resolution_status || "pending_review";
  const isCard = variant === "card";
  const altText = comp.image_alt_text || comp.prize_display_name || comp.prize_name || comp.title;
  const displayImageUrl = publicImageUrlForCompetition(comp);
  const action = showOutboundOverlay ? sourceActionForCompetition(comp) : null;
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [displayImageUrl]);

  const showImage =
    !imageLoadFailed &&
    !!displayImageUrl &&
    (status === "exact_match" ||
      status === "close_match" ||
      status === "generated_render" ||
      status === "source_match" ||
      comp.image_public_trusted === true);

  // ----- Image render path --------------------------------------------------
  if (showImage) {
    const chip = comp.asset_chip_label;
    // Source overlay only attaches to operator-sourced and admin-verified
    // images. Curated registry images keep the "Representative image" chip so
    // users aren't misled into thinking the operator supplied the photo.
    const showOverlay = Boolean(action);
    const image = (
      <img
        src={displayImageUrl}
        alt={altText}
        onError={() => setImageLoadFailed(true)}
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover ${isCard ? "transition-transform duration-500 group-hover:scale-105" : ""}`}
        data-testid={isCard ? `card-image-${comp.slug}` : "detail-image"}
      />
    );
    return (
      <>
        {action ? <SourceOverlay overlay={action} comp={comp} variant={variant} imageLink>{image}</SourceOverlay> : image}
        {showOverlay && (
          <SourceOverlay overlay={action} comp={comp} variant={variant} />
        )}
        {chip && (
          <span
            data-testid={isCard ? `card-image-chip-${comp.slug}` : "detail-image-chip"}
            data-chip-kind={status}
            className={`absolute right-2 top-2 ${isCard ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"} inline-flex items-center gap-1 rounded-full font-semibold bg-black/70 text-white backdrop-blur-sm`}
            title={
              status === "generated_render"
                ? "Generated stand-in render of the prize family. Not the actual prize unit."
                : "Image illustrates the prize family; not the actual prize unit."
            }
          >
            {chip}
          </span>
        )}
      </>
    );
  }

  if (imageLoadFailed) {
    return <>
      <ImageUnavailableTile comp={comp} variant={variant} />
      {action && <SourceOverlay overlay={action} comp={comp} variant={variant} />}
    </>;
  }

  // ----- Brand / category fallback tile -------------------------------------
  if (status === "brand_fallback" || status === "category_fallback") {
    return (
      <><BrandTile
        label={comp.brand_fallback_label || comp.prize_make?.toUpperCase() || "PRIZE"}
        category={comp.brand_fallback_category}
        prizeName={comp.prize_display_name || comp.prize_name || comp.title}
        variant={variant}
        comp={comp}
      />{action && <SourceOverlay overlay={action} comp={comp} variant={variant} />}</>
    );
  }

  // ----- Subtle placeholder for truly unknown / mismatch --------------------
  return (
    <><div
      className={`absolute inset-0 bg-gradient-to-br ${isCard ? "from-slate-300 to-slate-400" : "from-slate-700 to-slate-900"} flex items-center justify-center`}
      data-testid={isCard ? `card-image-placeholder-${comp.slug}` : "detail-image-placeholder"}
    >
      <div className="text-center px-4">
        <div className={`overline ${isCard ? "text-slate-600" : "text-white/50"} mb-1`}>
          Source image pending
        </div>
        <div className={`text-xs font-medium line-clamp-2 ${isCard ? "text-slate-700" : "text-white/80"}`}>
          {comp.prize_display_name || comp.prize_name || comp.title}
        </div>
      </div>
    </div>{action && <SourceOverlay overlay={action} comp={comp} variant={variant} />}</>
  );
}

// ---------------------------------------------------------------------------

function BrandTile({ label, category, prizeName, variant, comp }) {
  const isCard = variant === "card";
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-4"
      data-testid={isCard ? `card-brand-tile-${comp.slug}` : "detail-brand-tile"}
      data-brand-label={label}
      style={{
        background:
          "radial-gradient(120% 90% at 50% 0%, #0c172e 0%, #07101f 60%, #050a16 100%)",
      }}
    >
      <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--cc-gold)] to-transparent opacity-60" />
      <div className={`overline text-[var(--cc-gold)]/80 mb-2 tracking-[0.18em] ${isCard ? "text-[10px]" : "text-[11px]"}`}>
        {category || "Prize"}
      </div>
      <div
        className={`font-display font-semibold text-white leading-none tracking-tight ${
          isCard ? "text-2xl sm:text-3xl" : "text-4xl sm:text-5xl"
        }`}
      >
        {label}
      </div>
      <div
        className={`mt-3 text-white/70 font-medium max-w-[90%] line-clamp-2 ${
          isCard ? "text-xs" : "text-sm"
        }`}
      >
        {prizeName}
      </div>
      <div className={`mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/40`}>
        <Info size={10} weight="fill" /> Source image pending review
      </div>
      <div className="absolute bottom-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--cc-gold)] to-transparent opacity-30" />
    </div>
  );
}

// ---------------------------------------------------------------------------

export function SourceOverlay({ overlay, comp, variant, inline = false, testId, imageLink = false, children }) {
  const isCard = variant === "card";

  // Click → ping the outbound tracker with `image_overlay` context, then open
  // Anchor navigation owns the new tab; tracking is fire-and-forget so mobile
  // browsers keep the tap tied to a real href.
  const onClick = (e) => {
    e.stopPropagation();
    try {
      Promise.resolve(api.post(`/competitions/${comp.id}/click`, { context: "image_overlay" }))
        .catch(() => {});
    } catch {
      // Navigation belongs to the anchor even if tracking fails synchronously.
    }
  };

  try {
    const url = new URL(overlay.href);
    if (url.protocol !== "https:" || url.username || url.password) return null;
  } catch {
    return null;
  }

  const rel = overlay.is_affiliate
    ? "nofollow sponsored noopener noreferrer"
    : "nofollow noopener noreferrer";

  return (
    <a
      href={overlay.href}
      onClick={onClick}
      onKeyDown={(event) => event.stopPropagation()}
      rel={rel}
      target="_blank"
      data-testid={testId || (imageLink ? `prize-image-outbound-${comp.slug}` : isCard ? `card-source-overlay-${comp.slug}` : "detail-source-overlay")}
      data-affiliate={overlay.is_affiliate ? "true" : "false"}
      title={overlay.tooltip}
      aria-label="Visit operator site"
      className={imageLink ? "absolute inset-0" : `${inline ? "w-full justify-center" : `absolute right-2 ${isCard ? "bottom-2" : "bottom-3 right-3"}`} inline-flex items-center gap-1.5 rounded-md font-display font-semibold bg-black text-[var(--cc-gold)] border border-[var(--cc-gold)]/60 hover:border-[var(--cc-gold)] transition-colors ${
        isCard ? "text-[11px] px-3 py-1.5" : "text-xs px-3.5 py-2"
      }`}
    >
      {imageLink ? children : <>
        <span className="whitespace-normal text-center">Visit operator site</span>
        <ArrowSquareOut size={isCard ? 10 : 12} weight="bold" className="flex-none text-[var(--cc-gold)]" />
      </>}
    </a>
  );
}

function ImageUnavailableTile({ comp, variant }) {
  const isCard = variant === "card";
  return (
    <div
      className={`absolute inset-0 bg-gradient-to-br ${isCard ? "from-slate-300 to-slate-400" : "from-slate-700 to-slate-900"} flex items-center justify-center`}
      data-testid={isCard ? `card-image-unavailable-${comp.slug}` : "detail-image-unavailable"}
      data-image-state="browser-load-failed"
    >
      <div className="text-center px-4">
        <div className={`overline ${isCard ? "text-slate-700" : "text-white/65"}`}>
          Image unavailable
        </div>
      </div>
    </div>
  );
}
